// Hiring inside the Equall ERP shell (DPS-Blueprint EQUALL-SHELL-AND-DOMAINS.md).
//
// Three ways this app can be loaded, decided once, before React renders:
//
//   1. In the shell's frame on a campus address (dpspuneeast.equaos.in/hiring/).
//      The shell owns sign-in, the session, the school switcher and the top
//      bar. This app takes its access token and its user from
//      `window.parent.equall`, never signs in and never holds a refresh token.
//   2. Top level on a campus address, on a staff screen. Sent to the shell
//      (`/?open=<path>`), which opens the same screen in its frame.
//   3. Hiring's own app for one school (hiring.<school>.equaos.in), on a
//      staff screen: inside the EquaOS frame (@equaos/frame, frame.js), the
//      same frame People and every other app run in (owner, 29 Oct 2026:
//      "the apps look exactly the same … from the same UI frame"). The frame
//      owns sign-in, the session, the sidebar, the school, the person menu
//      and the assistant; Hiring takes its token and user from the frame's
//      context, exactly as from the ERP's shell.
//   4. Anywhere else (hiring.dpspuneeast.com, a candidate's link, local dev):
//      exactly as before. Own login, tokens in localStorage.
//
// Candidate and public screens are never sent to the shell and never need it:
// a candidate has no Equall login and must be able to open their link as is.

import { APP_BASE, SCHOOL_ADDRESS, PLATFORM_TIER } from './config/base';
import { erpPages } from './dashboard/adminPages';

/* ── Inside the EquaOS frame ───────────────────────────────────────────────
   The frame mounts Hiring with a context (ERP-Console docs/MICROFRONTENDS.md
   §3): getToken(), user, permissions, tenant. Until it does, nothing asks.
   This answers the same calls the ERP's window.equall does, so every screen,
   the axios instances and the storage shim below work unchanged. */
let frameCtx = null;

export function setFrameContext(ctx) {
  frameCtx = ctx || null;
}

const frameHost = {
  version: 1,
  frame: true,
  accessToken: () => (frameCtx ? frameCtx.getToken() : Promise.resolve(null)),
  me: () => {
    if (!frameCtx) return null;
    const u = frameCtx.user || {};
    return {
      id: u.id,
      email: u.email,
      name: u.name || u.displayName,
      permissions: frameCtx.permissions || [],
      adminScope: u.adminScope,
      isPlatformAdmin: u.adminScope === 'platform',
      tenant: frameCtx.tenant || null,
    };
  },
  // The frame lists Hiring's pages from the remote's meta.nav.
  setPages: () => true,
  // The frame follows the address itself (frame.js tells it of each move).
  navigated: () => {},
  onSessionChange: () => {},
};

/* A staff screen on Hiring's own address: in the frame. A candidate's link
   never is (they have no EquaOS login). */
function onFrameAddress() {
  if (typeof window === 'undefined' || window.parent !== window || !SCHOOL_ADDRESS) return false;
  return !isPublicRoute(window.location.pathname || '/');
}

function findShell() {
  if (onFrameAddress()) return frameHost;
  try {
    if (typeof window === 'undefined' || window.parent === window) return null;
    const equall = window.parent.equall;
    return equall && Number(equall.version) >= 1 ? equall : null;
  } catch {
    return null; // a parent on another origin is not our shell
  }
}


/* Candidate / public routes, relative to APP_BASE. Everything a candidate
   opens from a link, none of which needs a login. The last three are link
   shapes older invitations used; they are not routed today, but a candidate
   holding one must still never be bounced to a staff sign-in. */
const PUBLIC_ROUTES = [
  /^\/stage-interview(\/|$)/,
  /^\/stage-questionnaire(\/|$)/,
  /^\/test-complete\/?$/,
  /^\/pre-screening-test(\/|$)/,
  /^\/psychoanalytical-test(\/|$)/,
  /^\/final-interview(\/|$)/,
  // A candidate answering their offer, by its signed link (HIR-HR5).
  /^\/offer\/[^/]+\/?$/,
];

export function isPublicRoute(route) {
  return PUBLIC_ROUTES.some((re) => re.test(route || '/'));
}

export const shell = findShell();
export const inShell = Boolean(shell);
// Inside the EquaOS frame (Hiring's own address), not the ERP's iframe.
export const inFrame = Boolean(shell && shell.frame);

/* A campus (or group) address of the ERP, or a sslip.io test box. The legacy
   hiring.dpspuneeast.com is not one: there this app still runs on its own. */
export function isCampusHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h.endsWith('.equaos.in') || h === 'sslip.io' || h.endsWith('.sslip.io');
}

/* Where to send a top-level load of a staff screen on a campus address, or
   null to render here as usual. */
export function shellRedirectTarget() {
  if (typeof window === 'undefined' || inShell) return null;
  if (window.parent !== window) return null;              // framed by something else
  if (SCHOOL_ADDRESS) return null;                        // the school's own Hiring app
  if (!isCampusHost(window.location.hostname)) return null;
  const { pathname, search, hash } = window.location;
  const route = pathname.slice(APP_BASE.length) || '/';
  if (isPublicRoute(route)) return null;
  return '/?open=' + encodeURIComponent(pathname + search + hash);
}

/* ── Session, when in the shell ────────────────────────────────────────── */

let accessToken = null;   // last token the shell gave us; memory only
let shellUser = null;     // me(), in the shape this app stores as 'user'

export async function freshAccessToken() {
  accessToken = (await shell.accessToken()) || null;
  return accessToken;
}

/* The shell's me() → the `user` object this app has always kept in
   localStorage ({ id, email, name, role, companyName, logo }).

   `role` follows the API's own rule (auth.middleware roleFromClaims), so the
   Admin entry shows exactly when the API will honour it. `id` is the gateway
   identity id: the local record id is not known to the shell, and the one
   place the API needs it (continuing a JD) uses the job's owner instead. */
export function userFromMe(me) {
  if (!me) return null;
  const perms = me.permissions || [];
  const scope = me.adminScope;
  const role =
    me.isPlatformAdmin || (scope && scope !== 'none') || perms.includes('recruitment:admin')
      ? 'admin'
      : 'user';
  return {
    id: me.id,
    email: me.email || '',
    name: me.name || me.displayName || me.email || '',
    role,
    companyName: (me.tenant && me.tenant.name) || '',
    tenant: (me.tenant && me.tenant.slug) || '',
    logo: undefined,
  };
}

/* Every screen reads the session straight from localStorage ('token',
   'user'), in some thirty places. Rather than touch each of them, inside the
   shell those three keys are answered from memory: 'token' is the shell's
   latest access token, 'user' is built from me(), 'refreshToken' is always
   absent, and writes to them are dropped. This patches Storage in THIS
   frame's realm only; the shell's own window is untouched, and outside the
   shell this is never installed. */
const SESSION_KEYS = new Set(['token', 'refreshToken', 'user']);

function installSessionStorageShim() {
  let store;
  try { store = window.localStorage; } catch { return; }
  if (!store) return;
  const proto = Object.getPrototypeOf(store);
  const { getItem, setItem, removeItem } = proto;
  proto.getItem = function (key) {
    if (this === store && SESSION_KEYS.has(key)) {
      if (key === 'token') return accessToken;
      if (key === 'user') return shellUser ? JSON.stringify(shellUser) : null;
      return null;
    }
    return getItem.call(this, key);
  };
  proto.setItem = function (key, value) {
    if (this === store && SESSION_KEYS.has(key)) return undefined;
    return setItem.call(this, key, value);
  };
  proto.removeItem = function (key) {
    if (this === store && SESSION_KEYS.has(key)) return undefined;
    return removeItem.call(this, key);
  };
}

/* ── Hiring's pages in the ERP's own sidebar ──────────────────────────────
   One navigation, not two (Sanjoy, 3 Oct 2026): the ERP lists Hiring's pages
   under Hiring in its sidebar (equall.setPages, ERP release #34), tells this
   frame which one to show (an 'equall:navigate' message), and Hiring draws
   no rail of its own. An older ERP without setPages, or one that refuses the
   list, leaves Hiring's own rail in place. */
let pagesTaken = false;

export function shellPagesTaken() {
  return pagesTaken;
}

export function offerPages(isAdmin) {
  if (!inShell || typeof shell.setPages !== 'function') return false;
  try {
    pagesTaken = shell.setPages(erpPages(isAdmin, PLATFORM_TIER).map((p) => ({
      path: '/hiring' + p.value, label: p.label, icon: p.icon,
    }))) === true;
  } catch {
    pagesTaken = false;
  }
  return pagesTaken;
}

/* An ERP address inside Hiring ('/hiring/jobs?x=1') as Hiring's own route
   ('/jobs?x=1'), or null for anything else. */
export function erpRoute(path) {
  if (typeof path !== 'string' || !/^\/hiring(\/|\?|$)/.test(path)) return null;
  const route = path.replace(/^\/hiring/, '');
  return route.startsWith('/') ? route : `/${route}`;
}

/* The ERP's sidebar picked a page: `go` gets the route inside Hiring. Only
   the ERP itself (this frame's parent, same origin) is listened to. */
export function onShellNavigate(go) {
  if (!inShell) return () => {};
  const onMessage = (e) => {
    if (e.origin !== window.location.origin || e.source !== window.parent) return;
    const route = erpRoute(e.data && e.data.type === 'equall:navigate' ? e.data.path : null);
    if (route) go(route);
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

/* Called once before the first render, only when inShell. */
export async function bootShell() {
  installSessionStorageShim();
  try { shellUser = userFromMe(await Promise.resolve(shell.me())); } catch { shellUser = null; }
  offerPages(Boolean(shellUser && shellUser.role === 'admin'));
  try { await freshAccessToken(); } catch { accessToken = null; }
  try { shell.onSessionChange(() => window.location.reload()); } catch { /* older shell */ }
}

/* Keep the shell's address bar in step. `route` is the router's pathname,
   which is relative to the basename. */
export function reportRoute(route, search) {
  if (!inShell) return;
  try { shell.navigated('/hiring' + (route || '/') + (search || '')); } catch { /* ignore */ }
}

/* In the shell, every call on an axios instance asks the shell for the
   access token (the shell refreshes; we never do), and a 401 asks once more
   and retries once instead of logging out. A no-op outside the shell. */
export function attachShellAuth(instance) {
  if (!inShell) return instance;
  instance.interceptors.request.use(async (config) => {
    const token = await freshAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const original = error.config;
      if (error.response?.status === 401 && original && !original._shellRetried) {
        original._shellRetried = true;
        return instance(original); // the request interceptor fetches a new token
      }
      return Promise.reject(error);
    }
  );
  return instance;
}
